import useSidebarState from '../../hooks/useSidebarState';
import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { equalTo, get, onValue, orderByChild, query, ref as dbRef, serverTimestamp, set, update } from 'firebase/database';
import Sidebar from '../Sidebar';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { database } from '../../firebase';
import useTeamAccessProfile from '../../hooks/useTeamAccessProfile';
import useCompanyUID from '../../hooks/useCompanyUID';
import CommunityPostList from './CommunityPostList';
import {
  archivePost,
  createChannel,
  generateMagicLink,
  listMembers,
  listPosts,
} from '../../services/communityService';
import {
  buildCommunityChannelOptions,
  normalizeCommunityChannels,
  normalizeMemberRecord,
} from '../../utils/teamAccess';
import './Community.css';

const DEFAULT_CHANNELS = {
  talent_pool: {
    label: 'Talent Pool',
    audience: 'talent_pool',
    enabled: true,
    isDefault: true,
  },
  workforce: {
    label: 'Team Updates',
    audience: 'workforce',
    enabled: true,
    isDefault: true,
  },
};

const sortChannelOptions = (options = []) => (
  [...options].sort((left, right) => {
    const leftDefault = left.isDefault ? 0 : 1;
    const rightDefault = right.isDefault ? 0 : 1;
    if (leftDefault !== rightDefault) return leftDefault - rightDefault;
    return left.label.localeCompare(right.label);
  })
);

const slugifyChannelName = (value = '') => (
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
);

const getTeamProfileKey = (email = '') =>
  String(email || '')
    .trim()
    .toLowerCase()
    .replace(/[.#$/[\]]/g, '_');

const getTeamProfileStorageKey = (email = '', fallbackId = '') => {
  const normalizedEmail = String(email || '').trim().toLowerCase();
  return normalizedEmail ? getTeamProfileKey(normalizedEmail) : String(fallbackId || '').trim();
};

const CommunityAdminPage = () => {
  useUiLanguage();
  const { t, i18n } = useTranslation('community');
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { companyUID, loading: companyLoading } = useCompanyUID();
  const teamAccess = useTeamAccessProfile(companyUID);
  const [sidebarCollapsed, setSidebarCollapsed] = useSidebarState();
  const [posts, setPosts] = useState([]);
  const [members, setMembers] = useState([]);
  const [channels, setChannels] = useState(DEFAULT_CHANNELS);
  const [selectedFeed, setSelectedFeed] = useState('talent_pool');
  const [postsLoading, setPostsLoading] = useState(false);
  const [membersLoading, setMembersLoading] = useState(false);
  const [channelLoading, setChannelLoading] = useState(true);
  const [archivingPostId, setArchivingPostId] = useState('');
  const [inviteBusyByMember, setInviteBusyByMember] = useState({});
  const [memberSearch, setMemberSearch] = useState('');
  const [pageError, setPageError] = useState('');
  const [pageSuccess, setPageSuccess] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState('audience');
  const [editorCandidates, setEditorCandidates] = useState([]);
  const [channelDraft, setChannelDraft] = useState({
    name: '',
    audience: 'talent_pool',
    editorKeys: [],
  });
  const [channelSaving, setChannelSaving] = useState(false);
  const deferredMemberSearch = useDeferredValue(memberSearch);

  const sidebarWidth = sidebarCollapsed ? '60px' : '240px';
  const canManageChannels = teamAccess.isCompanyOwner || teamAccess.userPermissions.canManageCompanySettings;
  const canAssignEditors = canManageChannels || teamAccess.isCompanyOwner || teamAccess.userPermissions.canManageTeam;
  const communityFeatureEnabled = teamAccess.companyFeatures?.communityEnabled !== false;

  useEffect(() => {
    if (!companyUID) {
      setChannelLoading(false);
      return undefined;
    }

    setChannelLoading(true);
    const channelRef = dbRef(database, `companies/${companyUID}/community/channels`);
    const unsubscribe = onValue(channelRef, (snapshot) => {
      const nextValue = snapshot.exists() ? snapshot.val() || {} : {};
      setChannels({ ...DEFAULT_CHANNELS, ...nextValue });
      setChannelLoading(false);
    });

    return () => unsubscribe();
  }, [companyUID]);

  useEffect(() => {
    if (!drawerOpen || drawerTab !== 'channels' || !companyUID || !currentUser || !canAssignEditors) {
      setEditorCandidates([]);
      return;
    }

    let cancelled = false;

    const loadEditorCandidates = async () => {
      try {
        const collaboratorsRef = query(dbRef(database, 'collaborators'), orderByChild('companyId'), equalTo(companyUID));
        const legacyCollaboratorsRef = query(dbRef(database, 'collaborators'), orderByChild('company_uid'), equalTo(companyUID));
        const invitesRef = dbRef(database, `companies/${companyUID}/pendingInvites`);
        const teamProfilesRef = dbRef(database, `companies/${companyUID}/teamAccessProfiles`);

        const [collabSnapshot, legacyCollabSnapshot, invitesSnapshot, teamProfilesSnapshot] = await Promise.all([
          get(collaboratorsRef),
          get(legacyCollaboratorsRef),
          get(invitesRef),
          get(teamProfilesRef),
        ]);

        if (cancelled) return;

        const teamProfiles = teamProfilesSnapshot.exists() ? teamProfilesSnapshot.val() || {} : {};
        const results = [];
        const seen = new Map();

        const pushEntry = (entry) => {
          const normalized = normalizeMemberRecord(entry);
          const dedupeKey = (
            String(normalized.email || normalized.emailLower || '').trim().toLowerCase()
            || getTeamProfileStorageKey(normalized.email, normalized.uid || normalized.key)
            || `${normalized.uid || normalized.key || ''}`
          );
          if (!dedupeKey) return;

          const existingIndex = seen.get(dedupeKey);
          if (existingIndex !== undefined) {
            const existing = results[existingIndex] || {};
            results[existingIndex] = normalizeMemberRecord({
              ...normalized,
              ...existing,
              permissions: {
                ...(existing.permissions || {}),
                ...(normalized.permissions || {}),
              },
              communityChannels: normalizeCommunityChannels([
                ...(existing.communityChannels || []),
                ...(normalized.communityChannels || []),
              ]),
            });
            return;
          }

          seen.set(dedupeKey, results.length);
          results.push(normalized);
        };

        const pushCollaboratorSnapshot = (snapshot) => {
          if (!snapshot.exists()) return;
          snapshot.forEach((child) => {
            const data = child.val() || {};
            pushEntry({
              uid: child.key,
              sourceType: 'collaborator',
              ...data,
              ...(teamProfiles[child.key] || {}),
            });
          });
        };

        pushCollaboratorSnapshot(collabSnapshot);
        pushCollaboratorSnapshot(legacyCollabSnapshot);

        if (invitesSnapshot.exists()) {
          invitesSnapshot.forEach((child) => {
            const data = child.val() || {};
            pushEntry({
              key: child.key,
              uid: child.key,
              sourceType: 'invite',
              ...data,
              ...(teamProfiles[child.key] || {}),
            });
          });
        }

        Object.entries(teamProfiles).forEach(([profileKey, profileValue]) => {
          pushEntry({
            uid: profileValue?.uid || profileKey,
            key: profileKey,
            sourceType: 'profile',
            ...profileValue,
          });
        });

        setEditorCandidates(results);
      } catch (error) {
        console.error('Failed to load community editor candidates:', error);
      }
    };

    loadEditorCandidates();
    return () => {
      cancelled = true;
    };
  }, [canAssignEditors, companyUID, currentUser, drawerOpen, drawerTab]);

  const channelOptions = useMemo(
    () => sortChannelOptions(
      buildCommunityChannelOptions(channels).map((option) => ({
        ...option,
        audience: channels[option.value]?.audience || option.value,
        isDefault: channels[option.value]?.isDefault === true,
      }))
    ),
    [channels]
  );

  const channelLabelMap = useMemo(
    () => channelOptions.reduce((acc, option) => {
      acc[option.value] = option.label;
      return acc;
    }, {}),
    [channelOptions]
  );

  const availableFeeds = useMemo(() => {
    const enabledIds = channelOptions.map((channel) => channel.value);
    if (teamAccess.isCompanyOwner) {
      return enabledIds;
    }

    return normalizeCommunityChannels(teamAccess.allowedCommunityChannels || [], enabledIds);
  }, [channelOptions, teamAccess.allowedCommunityChannels, teamAccess.isCompanyOwner]);

  const canEditCommunity = communityFeatureEnabled && (
    teamAccess.isCompanyOwner || (teamAccess.userPermissions.canEditCommunity && availableFeeds.length > 0)
  );
  const canAccessWorkspace = canEditCommunity || canManageChannels || canAssignEditors;
  useEffect(() => {
    if (!availableFeeds.includes(selectedFeed)) {
      setSelectedFeed(availableFeeds[0] || 'talent_pool');
    }
  }, [availableFeeds, selectedFeed]);

  const clearMessages = () => {
    setPageError('');
    setPageSuccess('');
  };

  const loadPosts = async (channel) => {
    if (!currentUser || !channel || !canEditCommunity) return;
    try {
      setPostsLoading(true);
      const payload = await listPosts(currentUser, {
        channel,
        includeArchived: false,
        limit: 100,
      });
      setPosts(payload.posts || []);
    } catch (err) {
      console.error('Failed to load community posts:', err);
      setPageError(err.message || t('admin.errors.loadPostsFailed', 'Failed to load posts.'));
    } finally {
      setPostsLoading(false);
    }
  };

  const loadMembers = async (channel) => {
    if (!currentUser || !channel || !canAccessWorkspace) return;
    try {
      setMembersLoading(true);
      const payload = await listMembers(currentUser, { channel, limit: 200 });
      setMembers(payload.members || []);
    } catch (err) {
      console.error('Failed to load community members:', err);
      setPageError(err.message || t('admin.errors.loadMembersFailed', 'Failed to load members.'));
    } finally {
      setMembersLoading(false);
    }
  };

  useEffect(() => {
    if (!currentUser || companyLoading || !selectedFeed || !canAccessWorkspace) return;
    if (!availableFeeds.includes(selectedFeed) && !canManageChannels && !canAssignEditors) return;
    if (canEditCommunity && availableFeeds.includes(selectedFeed)) {
      loadPosts(selectedFeed);
    }
    loadMembers(selectedFeed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, companyLoading, selectedFeed, canEditCommunity, canAccessWorkspace, availableFeeds.join('|')]);

  const getMemberDisplayName = useCallback((member) => (
    member.fullName || member.displayName || member.name || member.emailLower || member.email || t('admin.members.noEmail', 'No email')
  ), [t]);

  const getMemberInitials = (member) => {
    const displayName = getMemberDisplayName(member);
    const parts = displayName.split(/\s+/).filter(Boolean).slice(0, 2);
    return parts.length > 0 ? parts.map((part) => part[0].toUpperCase()).join('') : '?';
  };

  const isMemberActiveForChannel = useCallback((member, channelId) => {
    const audience = channels[channelId]?.audience || channelId;
    return (member?.channels?.[audience]?.status || 'unsubscribed') === 'active';
  }, [channels]);

  const displayedPosts = useMemo(() => (Array.isArray(posts) ? posts : []), [posts]);

  const filteredMembers = useMemo(() => {
    const sourceMembers = members;
    const queryValue = deferredMemberSearch.trim().toLowerCase();

    return sourceMembers.filter((member) => {
      if (!queryValue) return true;
      const displayName = getMemberDisplayName(member).toLowerCase();
      const email = String(member.emailLower || member.email || '').toLowerCase();
      return displayName.includes(queryValue) || email.includes(queryValue);
    });
  }, [deferredMemberSearch, getMemberDisplayName, members]);

  const memberSummary = useMemo(() => {
    return filteredMembers.reduce((summary, member) => {
      summary.total += 1;
      if (isMemberActiveForChannel(member, selectedFeed)) {
        summary.active += 1;
      } else {
        summary.needsInvite += 1;
      }
      return summary;
    }, { total: 0, active: 0, needsInvite: 0 });
  }, [filteredMembers, isMemberActiveForChannel, selectedFeed]);

  const inviteCandidates = useMemo(
    () => filteredMembers.filter((member) => !isMemberActiveForChannel(member, selectedFeed)).slice(0, 5),
    [filteredMembers, isMemberActiveForChannel, selectedFeed]
  );

  const selectedEditorSet = useMemo(() => new Set(channelDraft.editorKeys), [channelDraft.editorKeys]);
  const availableEditorCandidates = useMemo(
    () => editorCandidates.filter((candidate) => candidate.uid || candidate.key || candidate.email),
    [editorCandidates]
  );

  const handleArchive = async (postId) => {
    if (!currentUser || !postId) return;
    clearMessages();
    try {
      setArchivingPostId(postId);
      await archivePost(currentUser, postId);
      setPosts((prev) => prev.filter((item) => item.id !== postId));
      setPageSuccess(t('admin.posts.archivedSuccess', 'Post archived.'));
    } catch (err) {
      console.error('Failed to archive post:', err);
      setPageError(err.message || t('admin.errors.archiveFailed', 'Failed to archive post.'));
    } finally {
      setArchivingPostId('');
    }
  };

  const handleGenerateLink = async (memberId) => {
    if (!currentUser || !memberId) return;
    clearMessages();
    try {
      setInviteBusyByMember((prev) => ({ ...prev, [memberId]: true }));
      const payload = await generateMagicLink(currentUser, memberId);
      if (payload.magic_link) {
        await navigator.clipboard.writeText(payload.magic_link);
      }
      setPageSuccess(t('admin.link.copied'));
    } catch (err) {
      console.error('Failed to generate member link:', err);
      setPageError(err.message || t('admin.link.copyFailed'));
    } finally {
      setInviteBusyByMember((prev) => ({ ...prev, [memberId]: false }));
    }
  };

  const handleToggleDraftEditor = (editorKey) => {
    setChannelDraft((prev) => ({
      ...prev,
      editorKeys: prev.editorKeys.includes(editorKey)
        ? prev.editorKeys.filter((entry) => entry !== editorKey)
        : [...prev.editorKeys, editorKey],
    }));
  };

  const assignChannelToEditor = async (member, channelId) => {
    const normalized = normalizeMemberRecord(member);
    const nextChannels = normalizeCommunityChannels(
      [...(normalized.communityChannels || []), channelId]
    );
    const nextPermissions = {
      ...(normalized.permissions || {}),
      canEditCommunity: true,
    };
    const profileKey = getTeamProfileStorageKey(normalized.email, normalized.uid || normalized.key);

    if (normalized.sourceType === 'collaborator' && normalized.uid) {
      await update(dbRef(database, `collaborators/${normalized.uid}`), {
        permissions: nextPermissions,
        communityChannels: nextChannels,
      });
    }

    if (normalized.sourceType === 'invite' && normalized.key) {
      await update(dbRef(database, `companies/${companyUID}/pendingInvites/${normalized.key}`), {
        permissions: nextPermissions,
        communityChannels: nextChannels,
      });
    }

    if (profileKey) {
      await set(dbRef(database, `companies/${companyUID}/teamAccessProfiles/${profileKey}`), {
        uid: normalized.uid || normalized.key || '',
        email: normalized.email,
        firstName: normalized.firstName,
        lastName: normalized.lastName,
        name: normalized.name,
        position: normalized.position,
        departmentScope: normalized.departmentScope,
        departments: normalized.departments,
        permissions: nextPermissions,
        communityChannels: nextChannels,
        updatedAt: serverTimestamp(),
      });
    }
  };

  const handleCreateChannel = async () => {
    if (!companyUID || !canManageChannels) return;

    const channelId = slugifyChannelName(channelDraft.name);
    if (!channelId) {
      setPageError('Channel name is required.');
      return;
    }
    if (channels[channelId]) {
      setPageError('A channel with this name already exists.');
      return;
    }

    clearMessages();
    setChannelSaving(true);
    try {
      const result = await createChannel(currentUser, {
        name: channelDraft.name.trim(),
        audience: channelDraft.audience,
        send_invites: true,
      });
      const createdChannelId = result.channel_id || channelId;

      const selectedEditors = availableEditorCandidates.filter((candidate) =>
        selectedEditorSet.has(`${candidate.sourceType}:${candidate.uid || candidate.key || candidate.email}`)
      );
      try {
        await Promise.all(selectedEditors.map((member) => assignChannelToEditor(member, createdChannelId)));
      } catch (editorError) {
        console.error('Channel created but editor assignment failed:', editorError);
        setPageError('Channel created, but editor access could not be assigned.');
      }

      setChannelDraft({
        name: '',
        audience: 'talent_pool',
        editorKeys: [],
      });
      setPageSuccess(
        `Channel created. ${result.emails_sent || 0} invites sent, ${result.emails_failed || 0} failed.`
      );
      setSelectedFeed(createdChannelId);
    } catch (error) {
      console.error('Failed to create community channel:', error);
      setPageError('Could not create the channel.');
    } finally {
      setChannelSaving(false);
    }
  };

  const accessLoading = teamAccess.loading || companyLoading || channelLoading;

  return (
    <div className="community-shell community-shell-feed">
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />

      <div
        className="community-body community-body-feed community-body-centered"
        dir={isRTL ? 'rtl' : 'ltr'}
        style={{
          marginInlineStart: sidebarWidth,
          transition: 'all 250ms ease',
          width: `calc(100vw - ${sidebarWidth})`,
        }}
      >
        <div className="community-stage">
          {accessLoading ? (
            <div className="community-public-card">
              <p className="community-skeleton">{t('admin.loadingPosts', 'Loading posts...')}</p>
            </div>
          ) : !communityFeatureEnabled ? (
            <div className="community-public-card">
              <p className="community-message error" style={{ marginTop: 0 }}>{uiText("Community is turned off for this workspace.")}</p>
            </div>
          ) : !canAccessWorkspace ? (
            <div className="community-public-card">
              <p className="community-message error" style={{ marginTop: 0 }}>{uiText("Community access is not enabled for your account.")}</p>
            </div>
          ) : (
            <>
              {pageError && <div className="community-message error">{pageError}</div>}
              {pageSuccess && <div className="community-message success">{pageSuccess}</div>}

              <div className="community-feed-panel">
                <div className="community-channel-heading">
                  <h2 className="community-channel-title">{channelLabelMap[selectedFeed] || selectedFeed}</h2>
                  <p className="community-channel-meta">
                    {memberSummary.total} {memberSummary.total === 1 ? uiText("member") : uiText("members")}
                  </p>
                </div>

                <div className="community-feed-toolbar community-feed-toolbar-centered">
                  <div className="community-feed-switcher" role="tablist" aria-label={uiText("Community channels")}>
                    {availableFeeds.map((channelId) => (
                      <button
                        key={channelId}
                        type="button"
                        className={`community-switch ${selectedFeed === channelId ? 'active' : ''}`}
                        onClick={() => setSelectedFeed(channelId)}
                      >
                        {channelLabelMap[channelId] || channelId}
                      </button>
                    ))}
                  </div>

                  <div className="community-stage-actions">
                    <button
                      type="button"
                      className="community-btn secondary"
                      onClick={() => {
                        setDrawerTab('audience');
                        setDrawerOpen(true);
                      }}
                    >{uiText("Members")}</button>

                    {canManageChannels && (
                      <button
                        type="button"
                        className="community-btn secondary"
                        onClick={() => {
                          setDrawerTab('channels');
                          setDrawerOpen(true);
                        }}
                      >{uiText("+ add channel")}</button>
                    )}

                    {canEditCommunity && (
                      <button
                        type="button"
                        className="community-btn community-publish-cta"
                        onClick={() => navigate('/community/publish')}
                      >
                        {t('admin.switcher.publish', 'Create update')}
                      </button>
                    )}
                  </div>
                </div>

                <div className="community-feed-scroll community-feed-scroll-centered">
                  {!canEditCommunity ? (
                    <div className="community-public-card">
                      <p className="community-muted">{uiText("You can manage channels and editor assignments here, but you are not assigned to publish into a feed.")}</p>
                    </div>
                  ) : postsLoading ? (
                    <p className="community-skeleton">{t('admin.loadingPosts', 'Loading posts...')}</p>
                  ) : (
                    <CommunityPostList
                      posts={displayedPosts}
                      locale={i18n.language}
                      channelLabels={channelLabelMap}
                      emptyText={t('admin.posts.empty')}
                      showArchive
                      onArchive={handleArchive}
                      archivingPostId={archivingPostId}
                      reactionLabels={{
                        archive: t('admin.posts.archive'),
                        totalPrefix: t('viewer.reactions.total', 'Total reactions'),
                        like: t('viewer.reactions.like'),
                        celebrate: t('viewer.reactions.celebrate'),
                        support: t('viewer.reactions.support'),
                      }}
                    />
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {drawerOpen && (
          <>
            <button
              type="button"
              className="community-drawer-backdrop"
              onClick={() => setDrawerOpen(false)}
              aria-label={uiText("Close drawer")}
            />
            <aside className="community-drawer">
              <div className="community-drawer-header">
                <div className="community-feed-switcher">
                  <button
                    type="button"
                    className={`community-switch ${drawerTab === 'audience' ? 'active' : ''}`}
                    onClick={() => setDrawerTab('audience')}
                  >{uiText("Audience")}</button>
                  {(canManageChannels || canAssignEditors) && (
                    <button
                      type="button"
                      className={`community-switch ${drawerTab === 'channels' ? 'active' : ''}`}
                      onClick={() => setDrawerTab('channels')}
                    >{uiText("Channels")}</button>
                  )}
                </div>

                <button type="button" className="community-btn secondary" onClick={() => setDrawerOpen(false)}>{uiText("Close")}</button>
              </div>

              {drawerTab === 'audience' ? (
                <div className="community-drawer-pane community-drawer-pane-audience">
                  <input
                    type="text"
                    className="community-search-input"
                    placeholder={t('admin.members.searchPlaceholder', 'Search people by name or email...')}
                    value={memberSearch}
                    onChange={(event) => setMemberSearch(event.target.value)}
                  />

                  {inviteCandidates.length > 0 && (
                    <div className="community-invite-panel">
                      <div className="community-card-title" style={{ fontSize: 15, marginBottom: 8 }}>{uiText("Invite queue")}</div>
                      <div className="community-invite-list">
                        {inviteCandidates.map((member) => (
                          <div key={`invite-${member.id}`} className="community-invite-item">
                            <div className="community-member-avatar">{getMemberInitials(member)}</div>
                            <div className="community-invite-copy">
                              <div className="community-member-name">{getMemberDisplayName(member)}</div>
                              <div className="community-member-email">{member.emailLower || t('admin.members.noEmail', 'No email')}</div>
                            </div>
                            <button
                              type="button"
                              className="community-btn secondary"
                              disabled={Boolean(inviteBusyByMember[member.id])}
                              onClick={() => handleGenerateLink(member.id)}
                            >
                              {inviteBusyByMember[member.id] ? '...' : t('admin.members.copyInvite', 'Copy invite')}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="community-members-list">
                    {membersLoading ? (
                      <p className="community-skeleton">{t('admin.loadingMembers', 'Loading members...')}</p>
                    ) : filteredMembers.length === 0 ? (
                      <p className="community-muted">{uiText("No members match this search.")}</p>
                    ) : filteredMembers.map((member) => (
                      <div key={member.id} className="community-member-row">
                        <div className="community-member-avatar">{getMemberInitials(member)}</div>
                        <div className="community-member-main">
                          <div className="community-member-name">{getMemberDisplayName(member)}</div>
                          <div className="community-member-email">{member.emailLower || t('admin.members.noEmail', 'No email')}</div>
                        </div>
                        <span className={`community-mini-status ${isMemberActiveForChannel(member, selectedFeed) ? 'active' : 'inactive'}`}>
                          {isMemberActiveForChannel(member, selectedFeed) ? uiText("In channel") : uiText("Needs invite")}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="community-drawer-pane community-channel-admin">
                  {canManageChannels && (
                    <div className="community-channel-creator">
                      <div className="community-section-title" style={{ marginTop: 0 }}>{uiText("Create channel")}</div>
                      <div className="community-field-stack">
                        <input
                          type="text"
                          className="community-search-input"
                          placeholder={uiText("Channel name")}
                          value={channelDraft.name}
                          onChange={(event) => setChannelDraft((prev) => ({ ...prev, name: event.target.value }))}
                        />

                        <div className="community-channel-row">
                        <div className="community-section-title">{uiText("Audience")}</div>
                          {['talent_pool', 'workforce'].map((audience) => (
                            <button
                              key={audience}
                              type="button"
                              className={`community-channel-toggle ${channelDraft.audience === audience ? 'selected' : ''}`}
                              onClick={() => setChannelDraft((prev) => ({ ...prev, audience }))}
                            >
                              <i className={`fa-solid ${channelDraft.audience === audience ? 'fa-check-circle' : 'fa-circle'}`} />
                              <span>{audience === 'workforce' ? uiText("Employee Directory") : uiText("Talent Pool")}</span>
                            </button>
                          ))}
                        </div>

                        {canAssignEditors && (
                          <div className="community-editor-picker">
                            <div className="community-section-title">{uiText("Assign posting access")}</div>
                            <div className="community-editor-list">
                              {availableEditorCandidates.map((candidate) => {
                                const editorKey = `${candidate.sourceType}:${candidate.uid || candidate.key || candidate.email}`;
                                return (
                                  <button
                                    key={editorKey}
                                    type="button"
                                    className={`community-editor-chip ${selectedEditorSet.has(editorKey) ? 'active' : ''}`}
                                    onClick={() => handleToggleDraftEditor(editorKey)}
                                  >
                                    <span className="community-member-avatar small">{getMemberInitials(candidate)}</span>
                                    <span>{getMemberDisplayName(candidate)}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        <button
                          type="button"
                          className="community-btn"
                          disabled={channelSaving}
                          onClick={handleCreateChannel}
                        >
                          {channelSaving ? uiText("Saving...") : uiText("Create channel")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </aside>
          </>
        )}
      </div>
    </div>
  );
};

export default CommunityAdminPage;
